import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatIDR, fileUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import StockPill from "../components/StockPill";
import JubelioBadge from "../components/JubelioBadge";
import { ShareMenu, CopySkuButton } from "../components/ProductActions";
import { timeAgo } from "../lib/share";
import { ArrowLeft, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MoveMappingModal from "../components/MoveMappingModal";
import { canEdit, canDelete } from "../lib/roles";
import { formatProductName } from "../lib/productName";
import { shopeeUrl, ShopeeLogo } from "../lib/marketplace";

const EXTRA_FIELDS = [
    ["Material", "material"],
    ["Stone Type", "stone_type"],
    ["Stone Grade", "stone_grade"],
    ["Stone Color", "stone_color"],
    ["Stone Cut", "stone_shape"],
];

const LINK_FIELDS = [
    ["Shopee", "shopee_url"],
    ["Tokopedia", "tokopedia_url"],
    ["Website", "website_url"],
    ["Instagram / Content", "content_url"],
    ["Video", "video_url"],
];

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { settings } = useSettings();
    const [p, setP] = useState(null);
    const [active, setActive] = useState(0);
    const [lightbox, setLightbox] = useState(false);
    const [showAllVariants, setShowAllVariants] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/products/${id}`);
                setP(data);
            } catch {
                toast.error("Product not found");
                navigate("/catalog");
            }
        })();
    }, [id, navigate]);

    const onDelete = async () => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Product deleted");
            navigate("/catalog");
        } catch {
            toast.error("Failed to delete");
        }
    };

    if (!p) return <div className="text-sm text-neutral-400 py-24 text-center">Loading…</div>;

    const imgs = p.images?.length ? p.images : [];
    const main = imgs[active] || imgs[0];
    const hasVariants = p.variants?.length > 0;
    const outOfStockVariantCount = hasVariants
        ? p.variants.filter((v) => (Number(v.stock) || 0) <= 0).length
        : 0;
    const visibleVariants = hasVariants
        ? showAllVariants
            ? p.variants
            : p.variants.filter((v) => (Number(v.stock) || 0) > 0)
        : [];
    const showCarat = (p?.brand || "hoshizora") === "hoshizora";
    const showSellingPoints = settings.show_selling_points !== false;
    const populatedExtras = (showCarat ? [...EXTRA_FIELDS, ["Carat", "carat"]] : EXTRA_FIELDS).filter(([, k]) => hasValue(p[k]));
    const populatedLinks = LINK_FIELDS.filter(([, k]) => hasValue(p[k]));
    const hasDescription = hasValue(p.description) || (showSellingPoints && hasValue(p.product_knowledge));
    const showNotes = hasValue(p.internal_notes) && canEdit(user?.role);

    const syncLabel =
        p.sync_status === "synced"
            ? `Synced · ${timeAgo(p.last_synced_at)}`
            : p.sync_status === "error"
              ? "Sync error"
              : p.sync_status === "pending"
                ? "Sync pending"
                : `Updated ${timeAgo(p.updated_at)}`;

    return (
        <div className="animate-fade-up w-full max-w-full">
            <Link to="/catalog" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-sage-dark">
                <ArrowLeft size={14} weight="light" /> Back to Catalog
            </Link>

            {/* Top: gallery + core info */}
            <div className="grid md:grid-cols-2 gap-6 md:gap-12 mt-4">
                {/* 1. Product Gallery */}
                <div className="min-w-0">
                    <div
                        className="aspect-square bg-[#F6F5F2] overflow-hidden rounded-sm flex items-center justify-center p-6 cursor-zoom-in"
                        onClick={() => main?.path && setLightbox(true)}
                        data-testid="product-main-image"
                    >
                        {main?.path ? (
                            <img src={fileUrl(main.path)} alt={p.name} className="w-full h-full object-contain" loading="eager" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs uppercase tracking-widest">
                                No photo
                            </div>
                        )}
                    </div>
                    {imgs.length > 1 && (
                        <>
                            <div className="flex items-center justify-center gap-1.5 mt-3 md:hidden">
                                {imgs.map((_, i) => (
                                    <span
                                        key={i}
                                        className={`h-1.5 rounded-full transition-all ${
                                            i === active ? "w-5 bg-sage-dark" : "w-1.5 bg-neutral-300"
                                        }`}
                                    />
                                ))}
                            </div>
                            <div className="grid grid-cols-5 gap-2 mt-3">
                                {imgs.map((im, i) => (
                                    <button
                                        key={im.id || i}
                                        onClick={() => setActive(i)}
                                        className={`aspect-square bg-[#F6F5F2] rounded-sm overflow-hidden border p-1.5 ${
                                            active === i ? "border-sage-dark" : "border-mist"
                                        }`}
                                        data-testid={`thumb-${i}`}
                                    >
                                        {im.path && (
                                            <img src={fileUrl(im.path)} alt="" className="w-full h-full object-contain" loading="lazy" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Core info */}
                <div className="min-w-0 space-y-5">
                    {/* 4. Category */}
                    <div className="eyebrow">{p.category}</div>

                    {/* 2. Product Name */}
                    <h1 className="font-sans font-medium text-xl md:text-2xl leading-[1.3] tracking-tight break-words" data-testid="detail-title">
                        {formatProductName(p.name)}
                    </h1>
                    {/* 3. SKU + status badges */}
                    <div className="flex items-center flex-wrap gap-2 md:gap-3 font-sans">
                        <span className="font-mono text-sm font-medium text-ink">{p.sku}</span>
                        <CopySkuButton sku={p.sku} size={14} testId={`copy-sku-${p.sku}`} />
                        <JubelioBadge product={p} size={16} />
                        <StockPill status={p.stock_status} />
                        <span
                            className="inline-flex items-center text-[10px] uppercase tracking-widest text-neutral-500"
                            data-testid="last-sync-indicator"
                            title="Jubelio sync status (preparing)"
                        >
                            {syncLabel}
                        </span>
                    </div>

                    {/* 5 + 6. Retail Price + Stock (Inter) */}
                    <div className="flex items-end gap-8 md:gap-12 flex-wrap font-sans">
                        <div>
                            <div className="eyebrow flex items-center gap-1">
                                Retail Price <JubelioBadge product={p} size={11} />
                            </div>
                            <div
                                className="font-sans font-semibold text-ink tabular-nums text-2xl md:text-3xl mt-1 tracking-tight"
                                data-testid="detail-retail-price"
                            >
                                {formatIDR(p.retail_price)}
                            </div>
                        </div>
                        <div>
                            <div className="eyebrow flex items-center gap-1">
                                Stock <JubelioBadge product={p} size={11} />
                            </div>
                            <div
                                className="font-sans font-semibold text-ink tabular-nums text-2xl md:text-3xl mt-1"
                                data-testid="detail-stock"
                            >
                                {p.stock}
                            </div>
                        </div>
                    </div>

                    {/* 7. Share + Edit/Delete */}
                    <div className="flex items-center gap-2 mt-4 flex-wrap font-sans" data-testid="detail-actions">
                        <ShareMenu product={p} />
                        {canEdit(user?.role) && (
                            <span className="ml-auto flex items-center gap-2">
                                <Link
                                    to={`/products/${p.id}/edit`}
                                    state={{ returnTo: `/products/${p.id}` }}
                                    data-testid="edit-product-button"
                                    className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep"
                                >
                                    <PencilSimple size={14} weight="light" /> Edit
                                </Link>
                                {canDelete(user?.role) && (
                                    <button
                                        onClick={onDelete}
                                        data-testid="delete-product-button"
                                        className="inline-flex items-center gap-1 border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-red-600 hover:text-red-600"
                                    >
                                        <Trash size={14} weight="light" /> Delete
                                    </button>
                                )}
                            </span>
                        )}
                        {canDelete(user?.role) && p.jubelio_product_id && (
                            <button onClick={() => setMoveOpen(true)} data-testid="move-mapping-button"
                                className="w-full sm:w-auto inline-flex items-center gap-1.5 border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark">
                                <JubelioBadge mapped size={12} /> Move Jubelio Mapping to Variant
                            </button>
                        )}
                    </div>
                    {moveOpen && (
                        <MoveMappingModal product={p} onClose={() => setMoveOpen(false)} onDone={(d) => { setP(d); setMoveOpen(false); }} />
                    )}
                </div>
            </div>

            {/* Scrollable sections below */}
            <div className="mt-10 md:mt-14 space-y-10 md:space-y-14 max-w-4xl">
                {/* 8. Description */}
                {hasDescription && (
                    <Section title="Description" testId="section-description">
                        {hasValue(p.description) && (
                            <p className="text-neutral-700 whitespace-pre-wrap text-sm md:text-base leading-relaxed">
                                {p.description}
                            </p>
                        )}
                        {hasValue(p.product_knowledge) && showSellingPoints && (
                            <div className={hasValue(p.description) ? "mt-5" : ""}>
                                <div className="eyebrow mb-2">Selling Points</div>
                                <p className="text-neutral-700 whitespace-pre-wrap text-sm md:text-base leading-relaxed">
                                    {p.product_knowledge}
                                </p>
                            </div>
                        )}
                    </Section>
                )}

                {/* 9. Variants */}
                {hasVariants && (
                    <Section
                        title={`Variants · ${visibleVariants.length}${outOfStockVariantCount && !showAllVariants ? ` of ${p.variants.length}` : ""}`}
                        testId="section-variants"
                        action={
                            outOfStockVariantCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => setShowAllVariants((v) => !v)}
                                    data-testid="toggle-show-all-variants"
                                    className="text-xs uppercase tracking-widest text-neutral-500 hover:text-sage-dark"
                                >
                                    {showAllVariants
                                        ? "Hide out-of-stock"
                                        : `Show ${outOfStockVariantCount} out-of-stock`}
                                </button>
                            ) : null
                        }
                    >
                        <div className="border border-mist rounded-sm overflow-hidden">
                            {/* Desktop table */}
                            <table className="hidden md:table w-full text-sm font-sans">
                                <thead>
                                    <tr className="text-left border-b border-mist bg-[#FAF9F6]">
                                        <th className="eyebrow p-3">Variant / Size</th>
                                        <th className="eyebrow p-3">SKU</th>
                                        {showCarat && <th className="eyebrow p-3">Carat</th>}
                                        <th className="eyebrow p-3">Price</th>
                                        <th className="eyebrow p-3">Stock</th>
                                        <th className="eyebrow p-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleVariants.map((v) => {
                                        const status =
                                            v.stock_status ||
                                            (v.stock <= 0
                                                ? "out_of_stock"
                                                : v.stock <= (settings.low_stock_threshold ?? 2)
                                                  ? "low_stock"
                                                  : "in_stock");
                                        return (
                                            <tr
                                                key={v.id}
                                                className="border-b border-mist last:border-0"
                                                data-testid={`variant-row-${v.sku}`}
                                            >
                                                <td className="p-3 font-sans font-medium text-ink">{v.label}</td>
                                                <td className="p-3 font-mono text-xs">
                                                    <span className="inline-flex items-center gap-1">
                                                        {v.sku}
                                                        <CopySkuButton sku={v.sku} />
                                                        <JubelioBadge mapped={v.jubelio_item_id != null} size={11} />
                                                    </span>
                                                </td>
                                                {showCarat && <td className="p-3 tabular-nums">{v.carat || "—"}</td>}
                                                <td className="p-3 whitespace-nowrap tabular-nums font-medium text-ink">
                                                    {formatIDR(v.retail_price)}
                                                </td>
                                                <td className="p-3 tabular-nums font-medium text-ink">{v.stock}</td>
                                                <td className="p-3"><StockPill status={status} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Mobile card list */}
                            <ul className="md:hidden divide-y divide-mist">
                                {visibleVariants.map((v) => {
                                    const status =
                                        v.stock_status ||
                                        (v.stock <= 0
                                            ? "out_of_stock"
                                            : v.stock <= (settings.low_stock_threshold ?? 2)
                                              ? "low_stock"
                                              : "in_stock");
                                    return (
                                        <li
                                            key={v.id}
                                            className="p-3 font-sans"
                                            data-testid={`variant-row-mobile-${v.sku}`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="font-medium text-ink">{v.label}</div>
                                                    <div className="font-mono text-[11px] text-neutral-500 mt-0.5 inline-flex items-center gap-1">
                                                        {v.sku}
                                                        <CopySkuButton sku={v.sku} />
                                                        <JubelioBadge mapped={v.jubelio_item_id != null} size={11} />
                                                    </div>
                                                    {v.carat && showCarat && (
                                                        <div className="text-[11px] text-neutral-500 mt-0.5 tabular-nums">
                                                            {v.carat} CT
                                                        </div>
                                                    )}
                                                </div>
                                                <StockPill status={status} />
                                            </div>
                                            <div className="mt-3 flex items-end justify-between gap-3">
                                                <div>
                                                    <div className="eyebrow text-[10px]">Price</div>
                                                    <div className="tabular-nums font-medium text-ink">
                                                        {formatIDR(v.retail_price)}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="eyebrow text-[10px]">Stock</div>
                                                    <div className="tabular-nums font-medium text-ink">{v.stock}</div>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </Section>
                )}

                {/* 10. Additional Information */}
                {populatedExtras.length > 0 && (
                    <Section title="Additional Information" testId="section-extra">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                            {populatedExtras.map(([label, key]) => (
                                <div key={key} className="border-b border-mist pb-3">
                                    <dt className="eyebrow">{label}</dt>
                                    <dd className="mt-1 text-ink break-words">{p[key]}</dd>
                                </div>
                            ))}
                        </dl>
                    </Section>
                )}

                {/* 11. Links */}
                {populatedLinks.length > 0 && (
                    <Section title="Links" testId="section-links">
                        <ul className="divide-y divide-mist border border-mist rounded-sm bg-white">
                            {populatedLinks.map(([label, key]) => (
                                <li key={key} className="flex items-center justify-between gap-4 px-4 py-3">
                                    <span className="eyebrow shrink-0 inline-flex items-center gap-1.5">{key === "shopee_url" && shopeeUrl(p[key]) && <ShopeeLogo size={14} />}{label}</span>
                                    <a
                                        href={p[key]}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm underline break-all min-w-0 truncate text-right text-sage-deep hover:text-sage-dark"
                                    >
                                        {p[key]}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

                {/* 12. Internal Notes */}
                {showNotes && (
                    <Section title="Internal Notes" testId="section-notes">
                        <p className="text-sm whitespace-pre-wrap text-neutral-700 leading-relaxed">
                            {p.internal_notes}
                        </p>
                    </Section>
                )}
            </div>

            {/* Lightbox */}
            <Dialog open={lightbox} onOpenChange={setLightbox}>
                <DialogContent className="max-w-4xl p-0 bg-ivory border-mist">
                    <VisuallyHidden asChild>
                        <DialogTitle>{p.name}</DialogTitle>
                    </VisuallyHidden>
                    <div className="relative aspect-square bg-[#F6F5F2] p-6">
                        {main?.path && (
                            <img src={fileUrl(main.path)} alt="" className="w-full h-full object-contain" />
                        )}
                        <button
                            onClick={() => setLightbox(false)}
                            className="absolute top-3 right-3 bg-white/90 rounded-full p-2 border border-mist hover:border-sage-dark"
                            aria-label="Close"
                            data-testid="lightbox-close"
                        >
                            <X size={16} weight="light" />
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Section({ title, testId, action, children }) {
    return (
        <section data-testid={testId}>
            <div className="flex items-end justify-between gap-3 mb-4">
                <h2 className="font-serif text-2xl md:text-3xl tracking-tight">{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}
